import React, { useState, useEffect } from "react";
import { useUser } from "../context/UserContext";
import { useCart } from "../context/CartContext";
import { useCheckout } from "../context/CheckoutContext";
import { useApi } from "../utils/Api";

function DeliveryForm({ notes, setNotes, setIsLoading, onClose }) {
  const { user, openLoginModal } = useUser();
  const { cartItems } = useCart();
  const { apiFetch } = useApi();

  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [postcodeConfirmed, setPostcodeConfirmed] = useState(false);
  const [lastConfirmedPostcode, setLastConfirmedPostcode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmingPostcode, setConfirmingPostcode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isOpen } = useCheckout();

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose(); 
      }, 60000); 

      return () => clearTimeout(timer); 
    }
  }, [isOpen, onClose]);

  /* ---------------- LOAD LAST ADDRESS ---------------- */
  useEffect(() => {
    setIsLoading(false);

    if (!user) return;

    const fetchLastDeliveryAddress = async () => {
      setIsLoading(true);
      try {
        const res = await apiFetch("last-delivery-address");
        if (res?.success) {
          setDeliveryAddress(res.delivery_address || "");
          setPostcode(res.postcode || "");
        }
      } catch {
      } finally {
        setIsLoading(false);
      }
    };

    fetchLastDeliveryAddress();

    // Cleanup in case component unmounts mid-fetch
    return () => setIsLoading(false);
  }, [user, apiFetch, setIsLoading]);

  /* ---------------- CONFIRM POSTCODE ---------------- */
  const confirmPostcode = async () => {
    if (!deliveryAddress.trim()) {
      setErrorMessage("Please enter a delivery address first");
      return;
    }

    if (!postcode.trim()) return;

    // Reuse previously confirmed fee if postcode hasn't changed
    if (postcode === lastConfirmedPostcode && deliveryFee !== null) {
      setPostcodeConfirmed(true);
      setErrorMessage("");
      return;
    }

    setConfirmingPostcode(true);
    setErrorMessage("");

    try {
      const res = await apiFetch("confirm-postcode", { postcode });

      if (!res.success) {
        setErrorMessage(res.message || "Invalid postcode");
        setDeliveryFee(null);
        setPostcodeConfirmed(false);
        return;
      }

      setDeliveryFee(Number(res.fee));
      setPostcodeConfirmed(true);
      setLastConfirmedPostcode(postcode);
    } catch {
      setErrorMessage("Error confirming postcode");
      setDeliveryFee(null);
      setPostcodeConfirmed(false);
    } finally {
      setConfirmingPostcode(false);
    }
  };

  /* ---------------- PRICE HELPERS ---------------- */
  const getItemTotals = (cartItem) => {
    const item = cartItem.item || cartItem;
    let total = Number(item.price || 0);

    if (cartItem.selectedRequiredOption) {
      total += Number(cartItem.selectedRequiredOption.price || 0);
    }

    [
      ...(item.swallowOptions || []),
      ...(item.proteins || []),
      ...(cartItem.selectedOptionalOptions || []),
    ].forEach((opt) => {
      total += Number(opt.option_price || 0) * (opt.quantity || 1);
    });

    return { total };
  };

  const cartTotal = cartItems.reduce((acc, c) => {
    const { total } = getItemTotals(c);
    return acc + total * (c.quantity || 1);
  }, 0);

  const SERVICE_CHARGE_RATE = 0.12;

  const serviceCharge =
    deliveryFee !== null
      ? Number(((cartTotal + deliveryFee) * SERVICE_CHARGE_RATE).toFixed(2))
      : 0;

  const grandTotal = Number(
    (cartTotal + serviceCharge + (deliveryFee || 0)).toFixed(2)
  );

  /* ---------------- BUILD EXTERNAL PAYLOAD ---------------- */
  const mapExtrasAndOptions = (cartItem) => {
    const item = cartItem.item;
    const itemQty = cartItem.quantity || 1;
    const extras = [];

    const pushExtra = (name, unitPrice, optQty = 1) => {
      const price = Number(unitPrice || 0);
      const quantity = optQty * itemQty;

      extras.push({
        name,
        price: price.toFixed(2),
        quantity,
        totalPrice: (price * quantity).toFixed(2),
      });
    };

    // Required option
    if (cartItem.selectedRequiredOption) {
      pushExtra(
        cartItem.selectedRequiredOption.name,
        cartItem.selectedRequiredOption.price,
        1
      );
    }

    // Optional options
    (cartItem.selectedOptionalOptions || []).forEach((opt) => {
      pushExtra(opt.option_name, opt.option_price, opt.quantity || 1);
    });

    // Swallow options
    (item.swallowOptions || []).forEach((opt) => {
      pushExtra(opt.option_name, opt.option_price, opt.quantity || 1);
    });

    // All proteins
    (item.proteins || []).forEach((opt) => {
      pushExtra(opt.option_name, opt.option_price, opt.quantity || 1);
    });

    return extras;
  };

  const buildExternalApiPayload = (orderId) => ({
    postcode,
    first_name: user?.first_name || "N/A",
    last_name: user?.last_name || "N/A",
    client_order_id: orderId,
    client_user_id: user?.id || null,
    contact_number: user?.phone || "",
    delivery_address: deliveryAddress,
    order_details: cartItems.map((c) => {
      const unitPrice = Number(c.item?.price || 0);
      const qty = c.quantity || 1;

      return {
        itemId: String(c.item?.id || c.item_id),
        itemName: c.item?.item_name || "Menu Item",
        itemPrice: unitPrice.toFixed(2),
        price: (unitPrice * qty).toFixed(2),
        quantity: String(qty),
        extras_and_options: mapExtrasAndOptions(c),
      };
    }),
    order_price: cartTotal.toFixed(2),
    delivery_fee: (deliveryFee || 0).toFixed(2),
    service_charge: serviceCharge.toFixed(2),
    email: user?.email || "",
    additional_details: notes || "",
  });

  /* ---------------- SAVE ORDER ---------------- */
  const saveOrderToDatabase = async () => {
    const orderItems = cartItems.map((c) => ({
      item_id: c.item?.id || c.item_id,
      base_price: c.item?.price || 0,
      required_option_id: c.selectedRequiredOption?.id || null,
      required_option_price: c.selectedRequiredOption?.price || 0,
      quantity: c.quantity || 1,
      selectedOptionalOptions: c.selectedOptionalOptions || [],
      swallowOptions: c.item?.swallowOptions || [],
      proteins: c.item?.proteins || [],
    }));

    return apiFetch("place-delivery-order", {
      customer_id: user?.id || null,
      customer_name: `${user?.first_name || ""} ${
        user?.last_name || ""
      }`.trim(),
      customer_phone: user?.phone || "",
      order_type: "Delivery",
      delivery_address: deliveryAddress,
      postcode,
      items: orderItems,
      total_price: cartTotal.toFixed(2),
      delivery_fee: (deliveryFee || 0).toFixed(2),
      service_charge: (serviceCharge || 0).toFixed(2),
      notes: notes.trim(),
    });
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      openLoginModal();
      return;
    }

    if (!postcodeConfirmed || deliveryFee === null) {
      setErrorMessage("Please confirm postcode");
      return;
    }

    if (cartItems.length === 0) {
      setErrorMessage("Your cart is empty");
      return;
    }

    if (submitting) return;

    setSubmitting(true);
    setIsLoading(true);
    setErrorMessage("");

    try {
      // 1️⃣ Save order to your database
      const dbResult = await saveOrderToDatabase();
      if (!dbResult.success) {
        setErrorMessage(dbResult.message || "Failed to save order");
        setSubmitting(false);
        setIsLoading(false);
        return;
      }

      // 2️⃣ Build external API payload
      const apiPayload = buildExternalApiPayload(dbResult.order_id);
      const result = await apiFetch("send-external-order", apiPayload);

      // 3️⃣ Handle payment redirect safely
      if (result.payment_url) {
        // Stop spinner before redirect
        setSubmitting(false);
        setIsLoading(false);

        // Small delay ensures React can render state changes
        setTimeout(() => {
          window.location.href = result.payment_url;
        }, 50);
        return;
      }

      // 4️⃣ Handle failure
      if (!result.success) {
        setErrorMessage(result.message || "Failed to send order");
        setSubmitting(false);
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Something went wrong");
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <label>Delivery Address</label>
      <input
        value={deliveryAddress}
        onChange={(e) => setDeliveryAddress(e.target.value)}
        required
      />

      <label>Postcode</label>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <input
          value={postcode}
          onChange={(e) => {
            const newPostcode = e.target.value;
            setPostcode(newPostcode);

            // Reset confirmation if postcode changes
            if (newPostcode !== lastConfirmedPostcode) {
              setPostcodeConfirmed(false);
              setDeliveryFee(null);
            }
          }}
          required
        />

        {!postcodeConfirmed && (
          <button
            type="button"
            className="confirm-postcode-btn"
            disabled={!deliveryAddress.trim() || confirmingPostcode}
            onClick={confirmPostcode}
          >
            {confirmingPostcode ? "Checking..." : "Confirm"}
          </button>
        )}
      </div>

      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      <label>Notes (optional)</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />

      <div className="price-breakdown" style={{ margin: "1rem 0" }}>
        <div className="price-row">
          <span>Order Price</span>
          <span>£{cartTotal.toFixed(2)}</span>
        </div>

        <div className="price-row">
          <span>Delivery Fee</span>
          <span>£{deliveryFee !== null ? deliveryFee.toFixed(2) : "0.00"}</span>
        </div>

        <div className="price-row">
          <span>Service Charge</span>
          <span>£{serviceCharge.toFixed(2)}</span>
        </div>

        <hr />

        <div className="price-row total">
          <span>
            <strong>Total</strong>
          </span>
          <span>
            <strong>£{grandTotal.toFixed(2)}</strong>
          </span>
        </div>
      </div>

      <button
        disabled={!postcodeConfirmed || submitting}
        className="add-to-cart-button send-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "0.5rem 1rem",
        }}
      >
        <span>{submitting ? "Processing..." : "Pay Now"}</span>
        <span>£{grandTotal.toFixed(2)}</span>
      </button>
    </form>
  );
}

export default DeliveryForm;
